import { useCallback, useRef } from "react";

/**
 * Hook for tracking cursor position in a textarea
 * Used for inserting dictated text at the cursor position
 *
 * @returns Object containing textarea ref, cursor position, and tracking function
 */
export function useTextareaCursorTracking() {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPositionRef = useRef<number | undefined>(undefined);

  /**
   * Track cursor position whenever it changes in the textarea
   * Should be called on onClick, onKeyUp, and onSelect events
   */
  const updateCursorPosition = useCallback(() => {
    if (textareaRef.current) {
      cursorPositionRef.current = textareaRef.current.selectionStart;
    }
  }, []);

  /**
   * Get the current cursor position and reset it
   * @returns The cursor position, or undefined if not tracked
   */
  const getCursorPosition = useCallback(() => {
    const position = cursorPositionRef.current;
    cursorPositionRef.current = undefined;
    return position;
  }, []);

  return {
    textareaRef,
    updateCursorPosition,
    getCursorPosition,
  };
}
